import { useMutation } from '@tanstack/react-query';
import { workspaceService } from '@/modules/workspace/api/workspace.service';
import { APP_ROUTES } from '@/api/constants/app.url';
import { setAuthToken } from '@/shared/utils/auth-token';
import { writeSessionCookie } from '@/shared/utils/cookies';
import { setStoredPlan } from '@/shared/utils/stored-plan';
import { setStoredAccess, type StoredAccess } from '@/shared/utils/stored-access';
import { rememberAccount } from '@/shared/utils/account-book';
import type { ApiError } from '@/api';
import type { DevLoginResponse } from '@/modules/auth/types';

/**
 * Switch the active workspace. The API returns a fresh login result (new token,
 * tenant, role, plan, permissions); we persist it with the same storage writes
 * as sign-in, then hard-reload into the dashboard home.
 *
 * Why a full reload rather than a soft SPA transition: role, sidebar nav,
 * permissions and plan all differ per workspace. A clean boot re-reads every
 * one of them from the freshly-written session/token instead of trying to
 * surgically invalidate a graph of role-scoped caches — the safest way to avoid
 * stale state bleeding across workspaces.
 */
export function useSwitchWorkspace() {
    return useMutation<DevLoginResponse, ApiError, string>({
        mutationFn: (tenantId: string) => workspaceService.switch(tenantId),
        onSuccess: (data) => {
            setAuthToken(data.token);
            const session = {
                userId:     data.user.id,
                email:      data.user.email,
                fullName:   data.user.full_name,
                tenantId:   data.tenant.id,
                tenantSlug: data.tenant.slug,
                tenantName: data.tenant.name,
                role:       data.tenant.role,
                isDevMode:  false,
            };
            writeSessionCookie(session);
            setStoredPlan(data.plan);
            const access: StoredAccess = { permissions: data.permissions };
            setStoredAccess(access);
            // This login's entry in the account book now points at a different
            // workspace, role and token — refresh it, or switching back to this
            // account later would restore the workspace it just left.
            rememberAccount({
                session,
                token:       data.token,
                plan:        data.plan ?? null,
                permissions: data.permissions ?? null,
            });

            // Full reload — see the doc comment above. Replaces history so the
            // pre-switch page isn't reachable via Back under the new role.
            window.location.replace(APP_ROUTES.HOME);
        },
    });
}
